import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight, faTag, faTrash } from '@fortawesome/pro-solid-svg-icons';
import AlertListTableRow from './AlertListTableRow/AlertListTableRow';
import SortableTableHeader from '../../../common/SortableTableHeader/SortableTableHeader';
import {
  fetchFilteredAlertList, refreshFilteredAlertList, selectAlertListColumnVisibility,
  selectActiveSearchParams, selectAllIds,
} from '../../../../redux/slices/alertListSlice/alertListSlice';
import ListSortType from '../../../../utility/types/listSortType';
import listAlertPropType from '../../../../utility/PropTypes/listAlertPropType';
import PaginatedAlertListNavigation from '../../PaginatedAlertListNavigation/PaginatedAlertListNavigation';
import useSelection from '../../../hooks/useSelection/useSelection';
import IndeterminateCheckbox from '../../../common/IndeterminateCheckbox/IndeterminateCheckbox';
import { connectModalContainerContextToComponent } from '../../../../contexts/ModalContext/ModalContext';
import ApplyTagsModal from '../../../guidance/tags/modals/ApplyTagsModal/ApplyTagsModal';
import TagUtility from '../../../../utility/TagUtility';
import { EntityType } from '../../../../utility/types/EntityType';
import BulkDeleteAlertModal from '../BulkDeleteAlertModal/BulkDeleteAlertModal';
import { RaaRoles } from '../../../../utility/SecurityRules';
import HasRolePrivileges from '../../../highorder/HasRolePrivileges';
import ListSelectionBanner from '../../../common/RaaLists/ListSelectionBanner/ListSelectionBanner';
import { selectTagList } from '../../../../redux/slices/tagListSlice/tagListSlice';

const columnLabelMap = [
  { label: 'Name', sortType: ListSortType.ALERT_NAME },
  { label: 'Source', sortType: ListSortType.SOURCE },
  { label: 'Tags', sortType: ListSortType.TAG },
  { label: 'Trigger', sortType: ListSortType.TRIGGER },
  { label: 'Clear', sortType: ListSortType.CLEAR },
  { label: 'Current', sortType: ListSortType.CURRENT },
  // { label: 'Actions', sortType: alertSortType.ACTION_COUNT },
];

/* istanbul ignore next */
function AlertListTable({ alertList, isPublicDashboard, setModalJSX }) {
  const dispatch = useDispatch();

  const {
    handleSelectAllOnPage,
    handleSelectEntireList,
    isAllSelected,
    isIndeterminate,
    isSelected,
    handleSelect,
    selected,
    clearSelected,
    getSelectedObjectsFromList,
  } = useSelection();

  const tagList = useSelector(selectTagList);
  const lastSearchParams = useSelector(selectActiveSearchParams);
  const alertListColumnVisibility = useSelector(selectAlertListColumnVisibility);
  const allAlertIds = useSelector(selectAllIds);
  const selectedSortType = lastSearchParams.sortType;
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const handleGetSortedList = (sort, sortType) => dispatch(
    fetchFilteredAlertList({ ...lastSearchParams, sort, sortType }),
  );

  const handleRefreshFilteredList = () => dispatch(refreshFilteredAlertList());

  const handleSetChecked = () => {
    if (isIndeterminate()) {
      return 'indeterminate';
    }
    if (isAllSelected(alertList)) {
      return 'true';
    }
    return 'false';
  };

  const handleDeleteBulk = () => {
    const selectedAlerts = getSelectedObjectsFromList(alertList);
    setModalJSX((<BulkDeleteAlertModal
      alertList={selectedAlerts}
      onClose={() => setModalJSX((<></>))}
      onDelete={() => handleSelectAllOnPage([])}
    />));
  };

  const handleOpenApplyTagsModal = () => {
    const selectedAlerts = getSelectedObjectsFromList(alertList);
    const { tagsAssociatedWithAllSelected, tagsAssociatedWithSomeSelected } = TagUtility
      .getTagAssociationsForSelectedAlerts(selectedAlerts, tagList.map((tag) => tag.id));
    setModalJSX((
      <ApplyTagsModal
        selectedIdList={selected}
        entityType={EntityType.ALERT}
        tagsAssociatedWithAll={tagsAssociatedWithAllSelected}
        tagsAssociatedWithSome={tagsAssociatedWithSomeSelected}
      />
    ));
  };

  return (
    <>
      <div className="clearfix pr-2 pt-2 pb-2">
        <div className="d-flex float-left">
          <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
            <>
              <IndeterminateCheckbox
                onChange={() => handleSelectAllOnPage(alertList)}
                checked={handleSetChecked()}
              />
              {
           selected.length > 0 && (
           <>
             <Button variant="link" onClick={handleDeleteBulk}>
               <FontAwesomeIcon
                 icon={faTrash}
                 size="lg"
                 className="text-muted"
               />
             </Button>
             <Button variant="link" onClick={handleOpenApplyTagsModal}>
               <FontAwesomeIcon
                 icon={faTag}
                 size="lg"
                 className="text-muted"
               />
             </Button>
           </>
           )
          }
            </>
          </HasRolePrivileges>
          <Button variant="link" onClick={handleRefreshFilteredList}>
            <FontAwesomeIcon
              icon={faRotateRight}
              size="lg"
              className="text-muted"
            />
          </Button>
        </div>
        <div className="float-right">
          <PaginatedAlertListNavigation />
        </div>
      </div>
      <ListSelectionBanner
        allEntityIds={allAlertIds}
        isAllSelected={isAllSelected(allAlertIds)}
        isAllPageOnlySelected={isAllSelected(alertList) && !isAllSelected(allAlertIds)}
        selected={selected}
        clearSelected={clearSelected}
        handleSelectAll={() => handleSelectEntireList(allAlertIds)}
      />
      <table className="table table-sm table-condensed table-striped my-1">
        <thead className="thead-light">
          <tr>
            <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
              <th>{' '}</th>
            </HasRolePrivileges>
            <th>{' '}</th>
            {
              columnLabelMap.map((col) => {
                if (alertListColumnVisibility[col.label] === true) {
                  return (
                    <React.Fragment key={col.label}>
                      <SortableTableHeader
                        onDescClick={() => handleGetSortedList('DES', col.sortType)}
                        label={col.label}
                        onAscClick={() => handleGetSortedList('ASC', col.sortType)}
                        isSelected={col.sortType === selectedSortType}
                      />
                    </React.Fragment>
                  );
                }
                return (<></>);
              })
            }
          </tr>
        </thead>
        <tbody>
          {alertList.map((alert, index) => (
            <React.Fragment key={index}>
              <AlertListTableRow
                alert={alert}
                isPublicDashboard={isPublicDashboard}
                index={index}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
                isSelected={isSelected(alert.id)}
                handleSetSelected={() => handleSelect(alert.id)}
                disableQuickActions={isSelected(alert.id)}
              />
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
}

AlertListTable.propTypes = {
  alertList: PropTypes.arrayOf(PropTypes.shape(listAlertPropType)).isRequired,
  isPublicDashboard: PropTypes.bool.isRequired,
  setModalJSX: PropTypes.func.isRequired,
};

export default connectModalContainerContextToComponent(AlertListTable);
